import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { Box, Skeleton, styled, Typography } from '@mui/material';

import { useBreadcrumbs } from 'Breadcrumb';
import { EditCourseFormContainer } from 'Courses/components/forms';
import {
  deleteParticipantDesktop,
  getCourse,
  getCourseJoinCode,
  getFreshStartDesktop,
  getMyDesktop,
  getParticipants,
  getTrainerDesktop,
  saveMachineIframeSize,
  startParticipantDesktop,
  stopParticipantDesktop,
  useSelectAreParticipantsLoading,
  useSelectCourse,
  useSelectCourseParticipants
} from 'Courses/data';
import {
  getActiveParticipantsCount,
  getExpectedParticipantsCount,
  getJoinedParticipantsCount,
  isCourseInSession
} from 'Courses/utils/participants-utils';
import { urls } from 'Router';
import { useAppDispatch } from 'Store';
import { Spacing } from 'Themes/spacing';
import { Typo } from 'Themes/typo';

import type { ISaveMachineIframeSizeRequest } from '@libs/models';

import { BasicFullscreenProvider } from '../../components/fullscreen-desktop-viewer/basic-fullscreen-context';
import { BasicFullscreenViewer } from '../../components/fullscreen-desktop-viewer/basic-fullscreen-viewer';
import { DesktopFullscreenProvider } from '../../components/fullscreen-desktop-viewer/desktop-fullscreen-context';
import { FullscreenDesktopViewer } from '../../components/fullscreen-desktop-viewer/fullscreen-desktop-viewer';

import { breadcrumbs as coursesBreadcrumbs } from '../courses-list/courses-page';

import { ListModeActions } from './participant-list/list-mode-actions/ListModeActions';
import { ParticipantLivePreviewList } from './participant-list/participant-live-preview-list/participant-live-preview-list';
import type { ListMode } from './participant-list/ParticipantList.consts';
import { ParticipantListConsts } from './participant-list/ParticipantList.consts';
import { CourseDevConfigPanel, SettingsButtonModal, useJoinCourseGroup } from './components';

import type { ParticipantModel } from 'Api';

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
`;

const TopRow = styled(Box)`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${Spacing.medium};
`;

const LeftButtonsContainer = styled(Box)`
  display: flex;
  gap: ${Spacing.small};
`;

const sizeToColumns = (size: number): number => {
  const mapping: Record<number, number> = {
    1: 6, // XS = 6 columns = smallest iframes
    2: 5, // S = 5 columns
    3: 4, // M = 4 columns
    4: 3, // L = 3 columns
    5: 2, // XL = 2 columns
    6: 1 // XXL = 1 column = largest iframes
  };

  return mapping[size] || 4;
};

export const CoursePage = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const course = useSelectCourse(id as string);
  const courseParticipants = useSelectCourseParticipants(id as string);

  const [columns, setColumns] = useState<number>(4);
  const [listMode, setListMode] = useState<ListMode>(ParticipantListConsts.defaultListMode);
  const [showEditModal, setShowEditModal] = useState(false);

  const requestedDataRef = useRef<Set<string>>(new Set());
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());

  const cancelAllRequests = () => {
    abortControllersRef.current.forEach((controller, key) => {
      controller.abort();
    });
    abortControllersRef.current.clear();
    requestedDataRef.current.clear();
  };

  useEffect(
    () => () => {
      cancelAllRequests();
    },
    []
  );

  const handleMachineIframeSize = (columns: number) => setColumns(columns);

  const handleOpenEditModal = () => {
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
  };

  useJoinCourseGroup(id as string);

  const areParticipantsLoading = useSelectAreParticipantsLoading(id as string);

  const joinLink = `${window.location.protocol}//${window.location.host}${urls.joinToCoursePage(
    course?.joinCode as string
  )}`;

  const courseFlags = course?.flags;
  const canEditCourse = Boolean(course?.id && !courseFlags?.isParticipant);

  const filteredParticipants = course?.showOnlyActiveMachines
    ? courseParticipants.filter(p => p.desktop?.isRunning)
    : courseParticipants;

  const inactiveUsersCount = course?.showOnlyActiveMachines
    ? courseParticipants.length - filteredParticipants.length
    : 0;

  const handleStartParticipantsMachines = () => {
    for (const participant of courseParticipants) {
      if (participant.desktop?.instanceId && !participant.desktop?.isRunning) {
        dispatch(
          startParticipantDesktop({
            courseId: id!,
            desktopInstanceId: participant.desktop?.instanceId,
            participantId: participant.id
          })
        );
      }
    }
  };

  const handleStopParticipantsMachines = () => {
    for (const participant of courseParticipants) {
      if (participant.desktop?.instanceId && participant.desktop?.isRunning) {
        dispatch(
          stopParticipantDesktop({
            courseId: id!,
            desktopInstanceId: participant.desktop?.instanceId,
            participantId: participant.id
          })
        );
      }
    }
  };

  const handleRemoveParticipantsMachines = () => {
    for (const participant of courseParticipants) {
      if (participant.desktop?.instanceId && !participant.desktop?.isRunning) {
        dispatch(
          deleteParticipantDesktop({
            courseId: id!,
            desktopInstanceId: participant.desktop?.instanceId,
            participantId: participant.id
          })
        );
      }
    }
  };

  const handleSaveMachineIframeSize = (machineIframeSize: number) => {
    dispatch(
      saveMachineIframeSize({
        courseId: id,
        machineIframeSize
      } as ISaveMachineIframeSizeRequest)
    );
  };

  useEffect(() => {
    const courseId = id as string;

    if (!course) {
      dispatch(getCourse(courseId)).then(() => {
        dispatch(getTrainerDesktop({ courseId }));
        dispatch(getMyDesktop({ courseId }));
        dispatch(getFreshStartDesktop({ courseId }));
        dispatch(getParticipants(courseId));
      });

      return;
    }

    if (
      course &&
      (!course.joinCode || course.joinCode === '') &&
      !course.isJoinCodeLoading &&
      !course.isJoinCodeLoaded
    ) {
      dispatch(getCourseJoinCode(courseId));
    }

    if (
      course &&
      !course.trainerDesktop &&
      !course.isTrainerDesktopLoading &&
      !course.isTrainerDesktopLoaded &&
      !course.isTrainer &&
      !requestedDataRef.current.has('trainerDesktop')
    ) {
      requestedDataRef.current.add('trainerDesktop');
      dispatch(getTrainerDesktop({ courseId }));
    }

    if (
      course.myDesktop === undefined &&
      !course.isMyDesktopLoading &&
      !course.isMyDesktopLoaded &&
      !requestedDataRef.current.has('myDesktop')
    ) {
      requestedDataRef.current.add('myDesktop');
      dispatch(getMyDesktop({ courseId }));
    }

    if (
      course.freshStartDesktop === undefined &&
      !course.isFreshStartDesktopLoading &&
      !course.isFreshStartDesktopLoaded &&
      !requestedDataRef.current.has('freshStartDesktop')
    ) {
      requestedDataRef.current.add('freshStartDesktop');
      dispatch(getFreshStartDesktop({ courseId }));
    }

    if (
      course.participants === undefined &&
      !course.areParticipantsLoading &&
      !course.areParticipantsLoaded &&
      !requestedDataRef.current.has('participants')
    ) {
      requestedDataRef.current.add('participants');
      dispatch(getParticipants(courseId));
    }
  }, [id, course, dispatch]);

  useEffect(() => {
    requestedDataRef.current.clear();
  }, [
    course?.id,
    course?.myDesktop,
    course?.freshStartDesktop,
    course?.trainerDesktop,
    course?.participants,
    course?.isMyDesktopLoaded,
    course?.isFreshStartDesktopLoaded,
    course?.isTrainerDesktopLoaded,
    course?.areParticipantsLoaded,
    course?.isJoinCodeLoaded
  ]);

  useEffect(() => {
    if (course?.machineIframeSize) {
      setColumns(sizeToColumns(course.machineIframeSize));
    }
  }, [course]);

  const showParticipantsInfo = course && isCourseInSession(course);
  const activeCount = getActiveParticipantsCount(courseParticipants, course ?? undefined);
  const joinedCount = getJoinedParticipantsCount(courseParticipants, course ?? undefined);
  const expectedCount = course ? getExpectedParticipantsCount(course) : 0;

  const breadcrumbLabel =
    course && showParticipantsInfo
      ? `${course.title} (${activeCount}/${joinedCount}/${expectedCount})`
      : (course?.title ?? '');

  const items = [
    ...coursesBreadcrumbs,
    {
      label: breadcrumbLabel,
      to: urls.coursePage(id as string)
    }
  ];

  useBreadcrumbs(items);

  return (
    <Container>
      {!areParticipantsLoading && course ? (
        <>
          <TopRow>
            <LeftButtonsContainer>
              {!courseFlags?.isFinished ? (
                <SettingsButtonModal
                  onStartParticipantsMachines={handleStartParticipantsMachines}
                  onStopParticipantsMachines={handleStopParticipantsMachines}
                  onRemoveParticipantsMachines={handleRemoveParticipantsMachines}
                  onEditCourse={handleOpenEditModal}
                  shouldShowStartParticipantsMachines={
                    !!(
                      courseParticipants.length &&
                      courseParticipants.some(x => x.desktop?.instanceId)
                    )
                  }
                  shouldShowStopParticipantsMachines={
                    !!(
                      courseParticipants.length &&
                      courseParticipants.some(x => x.desktop?.isRunning)
                    )
                  }
                  shouldShowRemoveParticipantsMachines={
                    !!(
                      courseParticipants.length &&
                      courseParticipants.some(x => x.desktop?.instanceId)
                    )
                  }
                  canEditCourse={canEditCourse}
                  joinLink={joinLink}
                  course={course}
                />
              ) : (
                <Typography variant={Typo.title}>{t('coursePanel.trainingCompleted')}</Typography>
              )}
            </LeftButtonsContainer>

            <ListModeActions
              listMode={listMode}
              defaultMachineIframeSize={course.machineIframeSize}
              onListModeChange={setListMode}
              onMachineIframeSizeChange={handleMachineIframeSize}
              onSaveMachineIframeSize={handleSaveMachineIframeSize}
            />
          </TopRow>

          <BasicFullscreenProvider>
            <DesktopFullscreenProvider>
              <FullscreenDesktopViewer course={course} />
              <ParticipantLivePreviewList
                course={course}
                participantList={filteredParticipants}
                trainer={
                  {
                    displayName: course.trainer?.displayName ?? 'Unknown Trainer',
                    avatar: course.trainer?.avatar,
                    id: course.trainer?.id ?? '',
                    desktop: course.isTrainer ? course.myDesktop : course.trainerDesktop
                  } as ParticipantModel
                }
                columns={columns}
                isMinimized={listMode === ParticipantListConsts.listMode.basicInfo}
                participantsMachineControlActive={course.participantsMachineControlActive ?? false}
                inactiveUsersCount={inactiveUsersCount}
              />
            </DesktopFullscreenProvider>

            <BasicFullscreenViewer />
          </BasicFullscreenProvider>

          {showEditModal && (
            <EditCourseFormContainer
              open={showEditModal}
              onClose={handleCloseEditModal}
              courseId={course.id}
            />
          )}
        </>
      ) : (
        ''
      )}

      {areParticipantsLoading ? (
        <Skeleton variant="rounded" animation="wave" width={200} height={200} sx={{ m: 'auto' }} />
      ) : (
        ''
      )}

      <CourseDevConfigPanel />
    </Container>
  );
};
